'use client';

import React from 'react';
import { usePathname } from 'next/navigation'; // useSearchParams
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { catalogItems } from '@/components/ui/catalog/catalog-data';
import { BreadcrumbItem } from '@/components/ui/layout/fixed-layout';

interface BreadcrumbProps {
  breadcrumbs?: BreadcrumbItem[];
  mobile?: boolean;
}

const truncateText = (text: string, maxLength: number = 30): string => {
  if (!text || text.length <= maxLength) return text;

  const halfLength = Math.floor((maxLength - 3) / 2);
  const start = text.substring(0, halfLength);
  const end = text.substring(text.length - halfLength);

  return `${start}...${end}`;
};

export function Breadcrumb({ breadcrumbs, mobile = false }: BreadcrumbProps) {
  // Eğer breadcrumbs parametresi gönderilmişse, onu kullan
  if (breadcrumbs && breadcrumbs.length > 0) {
    return (
      <div className="font-mono uppercase text-xs text-white flex items-center">
        <Link href="/" className="hover:text-zinc-300 transition-colors">
          ORKHAN
        </Link>

        {breadcrumbs.map((item, index) => (
          <React.Fragment key={index}>
            {mobile ? (
              // Mobil görünüm için - sadece son elemanı göster
              index === breadcrumbs.length - 1 && (
                <>
                  <span className="mx-1 text-zinc-500">/</span>
                  <span className={item.isActive ? 'text-zinc-300' : ''}>{truncateText(item.label)}</span>
                </>
              )
            ) : (
              // Normal görünüm için - tüm elemanları göster
              <>
                <span className="mx-1 text-zinc-500">/</span>
                {item.href ? (
                  <Link href={item.href} className="hover:text-zinc-300 transition-colors">
                    {truncateText(item.label)}
                  </Link>
                ) : (
                  <span className={item.isActive ? 'text-zinc-300' : ''}>{truncateText(item.label)}</span>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="font-mono uppercase text-xs text-white flex items-center">
      <Link href="/" className="hover:text-zinc-300 transition-colors">
        ORKHAN
      </Link>
    </div>
  );

  // Breadcrumbs parametresi gönderilmemişse, mevcut URL tabanlı sistemi kullan
  const pathname = usePathname();
  // const searchParams = useSearchParams();
  const [dropName, setDropName] = useState<string>('');
  const [articleTitle, setArticleTitle] = useState<string>('');

  // Define path mappings for breadcrumb labels
  const pathMappings: Record<string, string> = {
    '/catalog': 'ALL ARTWORKS',
    '/catalog/drops': 'DROPS',
    '/catalog/collections': 'COLLECTIONS',
    '/catalog/collection-preview': 'COLLECTIONS',
    '/catalog/installations': 'INSTALLATIONS',
    '/catalog/installation-preview': 'INSTALLATIONS',
    '/catalog/physical': 'PHYSICAL ARTWORKS',
    '/catalog/physicals': 'PHYSICAL ARTWORKS',
    '/catalog/immersive': 'IMMERSIVE',
    '/catalog/exhibitions': 'EXHIBITIONS',
    '/catalog/one-of-ones': 'ONE OF ONES',
    '/catalog/commissions': 'COMMISSIONS',
    '/catalog/commissions/:id': 'COMMISSIONS',
    '/insights/analytics': 'ANALYTICS',
    '/about': 'ABOUT',
    '/press': 'PRESS',
    '/writings': 'WRITINGS',
    '/insights': 'INSIGHTS',
    '/updates': 'UPDATES',
    '/collector': 'COLLECTOR ACCESS',
  };

  // Check if we're on a press detail page
  const isPressDetailPage = pathname.startsWith('/press/') && pathname !== '/press';
  const pressSlug = isPressDetailPage ? pathname.split('/').pop() : null;

  // Get article title from slug for press detail pages
  useEffect(() => {
    if (isPressDetailPage && pressSlug) {
      // Convert slug to a readable title
      const title = pressSlug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setArticleTitle(title);
    }
  }, [isPressDetailPage, pressSlug]);

  // Check if we're on the drop details page
  const isDropDetailPage = pathname === '/catalog/drop';
  // const id = isDropDetailPage ? searchParams.get('id') : null;
  const id = isDropDetailPage ?? null;

  // Check if we're on the collection preview page
  const isCollectionPreviewPage = pathname === '/catalog/collection-preview';
  // const collectionName = isCollectionPreviewPage ? searchParams.get('name') : null;
  const collectionName = isCollectionPreviewPage ?? null;

  // Check if we're on the installation preview page
  const isInstallationPreviewPage = pathname === '/catalog/installation-preview';
  // const installationId = isInstallationPreviewPage ? searchParams.get('id') : null;
  const installationId = isInstallationPreviewPage ?? null;

  // When on drop detail page, fetch the artwork title to use as the drop name
  useEffect(() => {
    if (isDropDetailPage && id) {
      const artwork = catalogItems.find(art => art.id.toString() === id.toString());
      if (artwork) {
        setDropName('ARTIFICIAL VISIONS'); // For now, hardcoded to match the design
        // Alternatively, use the actual artwork title
        // setDropName(artwork.displayTitle.toUpperCase());
      }
    }
  }, [isDropDetailPage, id]);

  // Get the current page label based on the pathname
  const currentPageLabel = pathMappings[pathname] || '';

  // Determine if we're on specific pages
  const isPhysicalsPage = pathname === '/catalog/physicals';
  const isAboutPage = pathname === '/about';
  const isPressPage = pathname === '/press';
  const isWritingsPage = pathname === '/writings';
  const isInsightsPage = pathname === '/insights';
  const isAnalyticsPage = pathname === '/insights/analytics';
  const isUpdatesPage = pathname === '/updates';
  const isCollectorPage = pathname === '/collector';
  const isOneOfOnesPage = pathname === '/catalog/one-of-ones';
  const isInstallationPage = pathname === '/catalog/installations';
  const isCatalogPage =
    pathname.startsWith('/catalog') &&
    pathname !== '/catalog/drop' &&
    pathname !== '/catalog/collection-preview' &&
    pathname !== '/catalog/installation-preview';
  const isPressRelatedPage = isPressPage || isPressDetailPage;

  return (
    <div className="font-mono uppercase text-xs text-white flex items-center">
      <Link href="/" className="hover:text-zinc-300 transition-colors">
        ORKHAN
      </Link>

      {isDropDetailPage ? (
        // Drop detail page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/catalog" className="hover:text-zinc-300 transition-colors">
            CATALOG
          </Link>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/catalog/drops" className="hover:text-zinc-300 transition-colors">
            DROPS
          </Link>
          {dropName && (
            <>
              <span className="mx-1 text-zinc-500">/</span>
              <span className="text-zinc-300">{dropName}</span>
            </>
          )}
        </>
      ) : isInstallationPreviewPage ? (
        // Installation preview page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/catalog" className="hover:text-zinc-300 transition-colors">
            CATALOG
          </Link>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/catalog/installations" className="hover:text-zinc-300 transition-colors">
            INSTALLATIONS
          </Link>
          {installationId && (
            <>
              <span className="mx-1 text-zinc-500">/</span>
              <span className="text-zinc-300">INSTALLATION {installationId}</span>
            </>
          )}
        </>
      ) : isCatalogPage ? (
        // Catalog subpages breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/catalog" className="hover:text-zinc-300 transition-colors">
            CATALOG
          </Link>
          {currentPageLabel && (
            <>
              <span className="mx-1 text-zinc-500">/</span>
              <span className="text-zinc-300">{currentPageLabel}</span>
            </>
          )}
        </>
      ) : isPressDetailPage ? (
        // Press detail page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/press" className="hover:text-zinc-300 transition-colors">
            PRESS
          </Link>
          {articleTitle && (
            <>
              <span className="mx-1 text-zinc-500">/</span>
              <span className="text-zinc-300">{articleTitle.toUpperCase()}</span>
            </>
          )}
        </>
      ) : isPressPage ? (
        // Main press page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">PRESS</span>
        </>
      ) : isAboutPage ? (
        // About page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">ABOUT</span>
        </>
      ) : isWritingsPage ? (
        // Writings page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">WRITINGS</span>
        </>
      ) : isInsightsPage ? (
        // Insights page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">INSIGHTS</span>
        </>
      ) : isAnalyticsPage ? (
        // Analytics page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <Link href="/insights" className="hover:text-zinc-300 transition-colors">
            INSIGHTS
          </Link>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">ANALYTICS</span>
        </>
      ) : isUpdatesPage ? (
        // Updates page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">UPDATES</span>
        </>
      ) : isCollectorPage ? (
        // Collector page breadcrumb
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">COLLECTOR ACCESS</span>
        </>
      ) : currentPageLabel ? (
        // Any other page with a mapping
        <>
          <span className="mx-1 text-zinc-500">/</span>
          <span className="text-zinc-300">{currentPageLabel}</span>
        </>
      ) : null}
    </div>
  );
}
